import { useMutation } from '@apollo/client'
import moment from 'moment-timezone'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  Button,
  ButtonColor,
  NotificationType,
  Select,
  TextField,
} from '../../../components'
import { UpdateCreatorResponse, UPDATE_CREATOR } from '../../../gql'
import {
  ChildAgeRange,
  Creator,
  CreatorOnboardingStep,
  CreatorPetsOwner,
  Ethnicity,
  Gender,
  MutationUpdateCreatorArgs,
} from '../../../gql/types'
import { AnalyticsEvent, trackEvent } from '../../../utils/analytics'
import {
  childAgeRangeLocalizedString,
  childAgeRangeLocalizedStringInverse,
  ethnicityLocalizedString,
  ethnicityLocalizedStringInverse,
  genderLocalizedString,
  genderLocalizedStringInverse,
  petLocalizedString,
  petLocalizedStringInverse,
} from '../../../utils/GqlStrings'
import { useNotification } from '../../../utils/hooks'
import { childAgeRangeOptions, petsOwnerOptions } from '../../../utils/mappers'
import { Bullets } from '../components/Bullets'
import { Steps } from '../components/Steps'
import Multiselect from '../../../elements/Multiselect'

export type EditCreatorProfileFormData = {
  gender: string
  ethnicity: string
  petsOwner: string[]
  occupation: string
  birthday: string
  childAgeRanges: string[]
}

interface Props {
  creator: Creator
}

const SetProfile = ({ creator }: Props) => {
  const defaultBirthday = moment().subtract(20, 'years').format('YYYY-MM-DD')
  const minBirthday = moment().subtract(100, 'years').format('YYYY-MM-DD')
  const maxBirthday = moment().subtract(16, 'years').format('YYYY-MM-DD')
  const { addNotification } = useNotification()
  const [updateCreator, { loading }] = useMutation<
    UpdateCreatorResponse,
    MutationUpdateCreatorArgs
  >(UPDATE_CREATOR)

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      gender: creator?.gender
        ? genderLocalizedString(creator.gender as Gender)
        : null,
      ethnicity: creator?.ethnicity
        ? ethnicityLocalizedString(creator.ethnicity as Ethnicity)
        : null,
      petsOwner: creator?.petsOwner
        ? creator.petsOwner.map((petOwner) => petLocalizedString(petOwner))
        : [],
      occupation: creator.occupation ?? null,
      birthday: creator?.birthday
        ? moment(creator.birthday).format('YYYY-MM-DD')
        : defaultBirthday, // null,
      childAgeRanges: creator?.childAgeRanges
        ? creator.childAgeRanges.map((childAgeRange) =>
            childAgeRangeLocalizedString(childAgeRange),
          )
        : [],
    },
  })

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewCreatorsOnboardingSetProfile)
  }, [])

  const onSubmit = async (data: EditCreatorProfileFormData) => {
    try {
      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            gender: genderLocalizedStringInverse(data.gender),
            ethnicity: ethnicityLocalizedStringInverse(data.ethnicity),
            petsOwner:
              data.petsOwner && data.petsOwner.length > 0
                ? data.petsOwner.map((petOwner) =>
                    petLocalizedStringInverse(petOwner),
                  )
                : null,
            occupation: data.occupation,
            birthday: data.birthday,
            childAgeRanges:
              data.childAgeRanges && data.childAgeRanges.length > 0
                ? data.childAgeRanges.map((childAgeRange) =>
                    childAgeRangeLocalizedStringInverse(childAgeRange),
                  )
                : null,
            onboarding: CreatorOnboardingStep.setAbout,
          },
        },
      })
    } catch (error) {
      console.log('@error SetProfile', error?.message)
      addNotification(error?.message, NotificationType.error)
    }
  }

  const goBack = async () => {
    try {
      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            onboarding: CreatorOnboardingStep.setSourceChannel,
          },
        },
      })
    } catch (error) {
      addNotification(error.message, NotificationType.error)
    }
  }

  return (
    <div className='min-h-screen flex flex-col'>
      <div className='flex-grow'>
        <Steps
          creator={creator}
          stepCurrent={CreatorOnboardingStep.setProfile}
        />

        <div className='min-h-full flex flex-col justify-center pt-4 px-4 md:mt-8 lg:px-8'>
          <div className='sm:mx-auto sm:w-full sm:max-w-2xl'>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className='md:shadow rounded-t-md sm:overflow-hidden mb-8'>
                <div className='py-5 md:bg-white space-y-6 sm:p-6'>
                  <div className='sm:mx-auto'>
                    <h2 className='mt-0 md:text-center text-3xl font-extrabold text-gray-900'>
                      My profile
                    </h2>
                    <p className='mt-2 md:text-center text-sm text-gray-600'>
                      We will share this information with brands to facilitate
                      the campaign process.
                    </p>
                  </div>

                  <div className='grid grid-cols-6 gap-6'>
                    <div className='col-span-6 sm:col-span-3'>
                      <Controller
                        name='gender'
                        control={control}
                        rules={{ required: `Please set a gender.` }}
                        render={({ field }) => (
                          <Select
                            options={Object.keys(Gender).map((gender) =>
                              genderLocalizedString(gender as Gender),
                            )}
                            field={field}
                            label='Gender'
                            error={errors?.gender?.message}
                          />
                        )}
                      />
                    </div>

                    <div className='col-span-6 sm:col-span-3'>
                      <Controller
                        name='ethnicity'
                        control={control}
                        rules={{ required: `Please set an ethnicity.` }}
                        render={({ field }) => (
                          <Select
                            options={Object.keys(Ethnicity).map((ethnicity) =>
                              ethnicityLocalizedString(ethnicity as Ethnicity),
                            )}
                            field={field}
                            label='Ethnicity'
                            error={errors?.ethnicity?.message}
                          />
                        )}
                      />
                    </div>

                    <div className='col-span-6 sm:col-span-3'>
                      <TextField
                        error={errors.occupation?.message}
                        label='Occupation'
                        placeholder='Magician'
                        {...register('occupation', {
                          required: `Please set your occupation.`,
                          minLength: {
                            value: 3,
                            message: `Your occupation must have more than 3 characters.`,
                          },
                          maxLength: {
                            value: 100,
                            message: `Your occupation cannot have more than 100 characters.`,
                          },
                        })}
                      />
                    </div>

                    <div className='col-span-6 sm:col-span-3'>
                      <TextField
                        type='date'
                        min={minBirthday}
                        max={maxBirthday}
                        error={errors.birthday?.message}
                        label='When is your birthday?'
                        // placeholder='dd/mm/yyyy'
                        {...register('birthday', {
                          required: `Please set your birthday.`,
                          validate: {
                            value: (birthday) => {
                              const age = moment().diff(
                                moment(birthday),
                                'years',
                              )
                              if (age >= 16) {
                                return true
                              }

                              return 'Make sure you are 16 or older.'
                            },
                          },
                        })}
                      />
                      {/* <p className='mt-1 block text-sm font-medium text-gray-500'>
                        We only show your age to potential matches, not your
                        birthday.
                      </p> */}
                    </div>

                    <div className='col-span-6 sm:col-span-3'>
                      <Controller
                        name='childAgeRanges'
                        control={control}
                        rules={{
                          required: `Please select at least one option.`,
                        }}
                        render={({ field }) => {
                          const options = childAgeRangeOptions.map(
                            (option) => ({
                              value: option.value,
                              label: childAgeRangeLocalizedString(option.value),
                            }),
                          )

                          const selectedOptions =
                            field.value?.map((localizedString) => {
                              const enumValue =
                                childAgeRangeLocalizedStringInverse(
                                  localizedString,
                                )
                              return {
                                value: enumValue,
                                label: childAgeRangeLocalizedString(enumValue),
                              }
                            }) || []

                          const handleChange = (selected) => {
                            if (!selected || selected.length === 0) {
                              field.onChange([])
                              return
                            }

                            const hasNoChildren = selected.some(
                              (option) =>
                                option.value === ChildAgeRange.noChildren,
                            )
                            const hasOtherChildren = selected.some(
                              (option) =>
                                option.value !== ChildAgeRange.noChildren,
                            )

                            // Prevent incompatible selections (automatically remove contradictory options)
                            if (hasNoChildren && hasOtherChildren) {
                              // If both "No Children" and other age ranges are selected, keep only the last selection
                              const lastSelection =
                                selected[selected.length - 1]
                              if (
                                lastSelection.value === ChildAgeRange.noChildren
                              ) {
                                field.onChange([
                                  childAgeRangeLocalizedString(
                                    ChildAgeRange.noChildren,
                                  ),
                                ])
                              }
                              if (
                                lastSelection.value !== ChildAgeRange.noChildren
                              ) {
                                // Keep only non-"No Children" options
                                field.onChange(
                                  selected
                                    .filter(
                                      (option) =>
                                        option.value !==
                                        ChildAgeRange.noChildren,
                                    )
                                    .map((option) =>
                                      childAgeRangeLocalizedString(
                                        option.value,
                                      ),
                                    ),
                                )
                              }
                            }

                            if (!hasNoChildren || !hasOtherChildren) {
                              field.onChange(
                                selected.map((option) =>
                                  childAgeRangeLocalizedString(option.value),
                                ),
                              )
                            }
                          }

                          return (
                            <div className='w-full'>
                              <label className='block text-sm leading-5 font-medium text-gray-700'>
                                Do you have children? Select their age.
                              </label>
                              <div className='mt-1'>
                                <Multiselect
                                  options={options}
                                  selected={selectedOptions}
                                  onChange={handleChange}
                                  placeholder='Select age...'
                                />
                              </div>
                              {errors?.childAgeRanges?.message && (
                                <p className='mt-2 text-sm text-red-600'>
                                  {errors.childAgeRanges.message}
                                </p>
                              )}
                            </div>
                          )
                        }}
                      />
                    </div>

                    <div className='col-span-6 sm:col-span-3'>
                      <Controller
                        name='petsOwner'
                        control={control}
                        rules={{
                          required: `Please select at least one option.`,
                        }}
                        render={({ field }) => {
                          const options = petsOwnerOptions.map((option) => ({
                            value: option.value,
                            label: petLocalizedString(option.value),
                          }))

                          const selectedOptions =
                            field.value?.map((localizedString) => {
                              const enumValue =
                                petLocalizedStringInverse(localizedString)
                              return {
                                value: enumValue,
                                label: petLocalizedString(enumValue),
                              }
                            }) || []

                          const handleChange = (selected) => {
                            if (!selected || selected.length === 0) {
                              field.onChange([])
                              return
                            }

                            const hasNoPets = selected.some(
                              (option) =>
                                option.value === CreatorPetsOwner.noPets,
                            )
                            const hasOtherPets = selected.some(
                              (option) =>
                                option.value !== CreatorPetsOwner.noPets,
                            )

                            // Prevent incompatible selections (automatically remove contradictory options)
                            if (hasNoPets && hasOtherPets) {
                              // If both "No Pets" and other pet types are selected, keep only the last selection
                              const lastSelection =
                                selected[selected.length - 1]
                              if (
                                lastSelection.value === CreatorPetsOwner.noPets
                              ) {
                                field.onChange([
                                  petLocalizedString(CreatorPetsOwner.noPets),
                                ])
                              }
                              if (
                                lastSelection.value !== CreatorPetsOwner.noPets
                              ) {
                                // Keep only non-"No Pets" options
                                field.onChange(
                                  selected
                                    .filter(
                                      (option) =>
                                        option.value !==
                                        CreatorPetsOwner.noPets,
                                    )
                                    .map((option) =>
                                      petLocalizedString(option.value),
                                    ),
                                )
                              }
                            }

                            if (!hasNoPets || !hasOtherPets) {
                              field.onChange(
                                selected.map((option) =>
                                  petLocalizedString(option.value),
                                ),
                              )
                            }
                          }

                          return (
                            <div className='w-full'>
                              <label className='block text-sm leading-5 font-medium text-gray-700'>
                                Do you have pets?
                              </label>
                              <div className='mt-1'>
                                <Multiselect
                                  options={options}
                                  selected={selectedOptions}
                                  onChange={handleChange}
                                  placeholder='Select your pets...'
                                  menuPlacement='top'
                                />
                              </div>
                              {errors?.petsOwner?.message && (
                                <p className='mt-2 text-sm text-red-600'>
                                  {errors.petsOwner.message}
                                </p>
                              )}
                            </div>
                          )
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className='flex justify-between md:shadow rounded-b-md py-3 md:bg-gray-50 text-right sm:px-6'>
                  <Button
                    title='Go back'
                    type='button'
                    colour={ButtonColor.white}
                    loading={loading}
                    disabled={loading}
                    onClick={goBack}
                  />
                  <Button
                    title='Continue'
                    type='submit'
                    loading={loading}
                    disabled={loading}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Bullets stepCurrent={CreatorOnboardingStep.setProfile} />
    </div>
  )
}

export { SetProfile }
